/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { Label } from '@fluentui/react/lib/Label';
import { Persona, PersonaSize } from '@fluentui/react/lib/Persona';
import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { ScrollBoundary } from '../../../../common';
import { getListContainerStyles, getPageStyles, getTitleStyles } from '../../PreSetupTemplateWizard.styles';

import {
    getBodyStyles,
    getChoiceGroupOptionStyles,
    getLabelStyles,
    getPersonaStyles,
    getStackItemStyles,
} from './ViewTeam.styles';

export interface TeamOption {
    id: string;
    name: string;
    pictureUrl?: string;
}

export interface ViewTeamComponentProps {
    onTeamSelected?: (teamId: string, teamName: string) => void;
    selectedTeamId?: string;
    teamOptions: TeamOption[];
    onNext?: () => void;
    onBack?: () => void;
}

export const ViewTeamComponent: React.FC<ViewTeamComponentProps> = (props: ViewTeamComponentProps): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const themeName = getAppHost().getTheme();

    const { onTeamSelected, selectedTeamId, onBack, onNext, teamOptions } = props;

    const options = React.useMemo((): IChoiceGroupOption[] => {
        return teamOptions.map((teamOption: TeamOption): IChoiceGroupOption => {
            const option: IChoiceGroupOption = {
                key: teamOption.id,
                text: teamOption.name,
                styles: getChoiceGroupOptionStyles(theme, themeName),

                onRenderLabel: (): JSX.Element => {
                    return (
                        <Stack horizontal verticalAlign='center' styles={getLabelStyles}>
                            <Persona
                                size={PersonaSize.size32}
                                styles={getPersonaStyles}
                                text={teamOption.name}
                                imageUrl={teamOption.pictureUrl}
                                hidePersonaDetails={true}
                            />
                            <Stack.Item styles={getStackItemStyles(theme)}>
                                <Text title={teamOption.name} block nowrap>
                                    {teamOption.name}
                                </Text>
                            </Stack.Item>
                        </Stack>
                    );
                },
            };
            return option;
        });
    }, [teamOptions, theme, themeName]);

    return (
        <Stack styles={getPageStyles}>
            <Stack.Item>
                <Label role='alert' aria-label={loc.strings.choose_team_page_aria_label} styles={getTitleStyles(theme)}>
                    {loc.strings.select_team_text}
                </Label>
            </Stack.Item>
            <Stack.Item styles={getBodyStyles} grow>
                <ScrollBoundary isTopBoundary={true} />
                <Stack styles={getListContainerStyles}>
                    {options?.length > 0 && (
                        <ChoiceGroup
                            onChange={(
                                ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
                                option?: IChoiceGroupOption
                            ): void => {
                                onTeamSelected(option.key, option.text);
                            }}
                            selectedKey={selectedTeamId}
                            options={options}
                        />
                    )}
                </Stack>
                <ScrollBoundary isTopBoundary={false} />
            </Stack.Item>
            <Stack.Item>
                <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
                    <ActionButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        text={loc.strings.carousel_back}
                        onClick={onBack}
                        ariaLabel={loc.strings.carousel_back_aria_label}
                    />
                    <PrimaryButton
                        aria-label={loc.strings.done}
                        disabled={!selectedTeamId}
                        text={loc.strings.done}
                        onClick={onNext}
                    />
                </Stack>
            </Stack.Item>
        </Stack>
    );
};
